import { useQuery } from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";
import { buildContactTimeline, TIMELINE_LIMIT, type TimelineEntry } from "@/utils/contacts";
import type { ConsentEvent, SmsMessage } from "@/types/sms";

export interface ContactHistory {
  timeline: TimelineEntry[];
  /** The newest consent event, for the "why" sentence beside the status pill. */
  latestConsent: Pick<ConsentEvent, "to_status" | "method" | "created_at"> | null;
  /** customers.created_at: when the POS relay first saw this number. */
  customerSince: string | null;
}

/**
 * One contact's consent events and texts in and out, for the Contacts detail
 * sheet. Only fetches while the sheet is open (a phone is given). The
 * sms_consent_events / sms_messages reads are store-scoped by RLS and indexed
 * on the phone; customers is the POS relay's table (SELECT-only for the
 * browser). Read-only.
 */
export const useContactHistory = (phone: string | null) => {
  const getSupabaseClient = useSupabaseClient();

  return useQuery<ContactHistory>({
    queryKey: ["sms-contact-history", phone],
    enabled: !!phone,
    queryFn: async () => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");
      const sb = supabase as unknown as SupabaseClient;

      const [events, messages, customer] = await Promise.all([
        sb
          .from("sms_consent_events")
          .select("id, to_status, method, raw_inbound, created_at")
          .eq("customer_phone", phone)
          .order("created_at", { ascending: false })
          .limit(TIMELINE_LIMIT),
        sb
          .from("sms_messages")
          .select("id, direction, kind, body, status, error_message, segments, sent_at, created_at")
          .eq("customer_phone", phone)
          .order("created_at", { ascending: false })
          .limit(TIMELINE_LIMIT),
        sb.from("customers").select("created_at").eq("customer_phone", phone).maybeSingle(),
      ]);
      if (events.error) throw events.error;
      if (messages.error) throw messages.error;
      if (customer.error) throw customer.error;

      const eventRows = (events.data ?? []) as Pick<
        ConsentEvent,
        "id" | "to_status" | "method" | "raw_inbound" | "created_at"
      >[];
      return {
        timeline: buildContactTimeline(eventRows, (messages.data ?? []) as SmsMessage[]),
        latestConsent: eventRows[0] ?? null,
        customerSince: (customer.data as { created_at: string } | null)?.created_at ?? null,
      };
    },
    staleTime: 60 * 1000,
  });
};
